import { useCallback, useSyncExternalStore } from 'react';
import { DateTime } from 'luxon';

import { getAuthorsByline, getLastPublicationDate } from '../../model/LoadedDocument';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { useCanStartCommentThread } from '../v2-hooks/useCanStartCommentThread';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

/** Presentation state for the read-only content title header. */
export interface ContentViewTitleViewModel {
  /** Long title with APGL entities already replaced. */
  readonly title: string;
  /** "Owner and co-authors" string for display. */
  readonly authorsByline: string;
  /** When the draft was last modified; `undefined` when unknown. */
  readonly lastModified: DateTime | undefined;
  /** Whether the commenting toggle button should be visible. */
  readonly isCommentToggleVisible: boolean;
  /** Whether commenting mode is currently active. */
  readonly isCommentingPressed: boolean;
  /** Toggle commenting mode on or off. */
  readonly toggleCommenting: (pressed: boolean) => void;
}

/**
 * Projects the loaded document into the title header's display values:
 * long title, authors byline, and last-modified date.
 * Returns undefined while no document handle is loaded — the view renders
 * nothing during that transient state rather than crashing.
 */
export function useContentViewTitleViewModel(): ContentViewTitleViewModel | undefined {
  const service = useContentEditorService();
  const commentManager = useInlineCommentManager();
  const convertEntitiesInText = useEntityConverter();
  const canStartCommentThread = useCanStartCommentThread();
  const document = useLoadedDocument();

  // When the commenting display toggle flips, then re-read it.
  const subscribeToCommentingMode = useCallback(
    (onStoreChange: () => void) => commentManager.on('viewCommentingModeChanged', onStoreChange),
    [commentManager],
  );
  const isViewCommentingEnabled = useSyncExternalStore(
    subscribeToCommentingMode,
    () => commentManager.isViewCommentingEnabled,
  );

  const toggleCommenting = useCallback(
    (pressed: boolean) => commentManager.toggleViewCommenting(pressed),
    [commentManager],
  );

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  const workflow =
    document.kind === 'draft' || document.kind === 'finalizing' ? document.workflow : undefined;

  return {
    title: convertEntitiesInText(document.content.contentMetadata.contentName ?? ''),
    authorsByline: getAuthorsByline(document),
    // Drafts show their last edit; publications have no draft workflow, so fall
    // back to the publication date.
    lastModified: workflow?.lastDraftUpdate ?? getLastPublicationDate(document),
    isCommentToggleVisible: canStartCommentThread,
    isCommentingPressed: isViewCommentingEnabled,
    toggleCommenting,
  };
}
